import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Bug, CheckCircle2, Lock, Send } from 'lucide-react';
import { http } from '../api/http';
import { studentMessagingError } from '../api/studentMessaging';
import { useAuthStore } from '../store/auth';
import { Button, Card, TextAreaField, TextField } from '../components/charte';

// Signaler un problème (refonte du 14/10/2026) : remplace l'écran ouvert par la coccinelle de la messagerie,
// comme dans l'espace élève. Même envoi qu'avant (`POST /v1/support/reports`, message + page) : le serveur relit
// lui-même le nom, l'adresse et l'école sur le compte et n'accepte pas d'autre champ. Ils sont donc affichés sans
// être modifiables, sauf l'adresse de réponse : si le moniteur la change, elle est écrite en tête du message.

/** Adresse de contact publique de Permio : le repli quand un signalement ne part pas. */
const SUPPORT_EMAIL = 'contact@permio.fr';
const MIN_LENGTH = 5;
const MAX_LENGTH = 4000;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ReadOnly({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1.5">
      <span className="text-[12.5px] font-medium text-secondary">{label}</span>
      <span className="flex h-11 items-center gap-2 rounded-xl border border-line bg-surface-2 px-3.5 text-sm text-secondary">
        <span className="min-w-0 flex-1 truncate">{value || '—'}</span>
        <Lock size={14} className="shrink-0 text-muted" aria-hidden />
      </span>
    </div>
  );
}

export default function SupportView({ onDone, doneLabel }: { onDone: () => void; doneLabel: string }) {
  const user = useAuthStore((s) => s.user);
  const accountEmail = user?.email ?? '';
  const [email, setEmail] = useState(accountEmail);
  const [message, setMessage] = useState('');
  const text = message.trim();
  const replyTo = email.trim();
  const otherEmail = replyTo !== '' && replyTo.toLowerCase() !== accountEmail.toLowerCase();
  const emailInvalid = replyTo !== '' && !EMAIL_PATTERN.test(replyTo);
  const body = otherEmail ? `Adresse de réponse indiquée : ${replyTo}\n\n${text}` : text;

  const send = useMutation({
    mutationFn: async () =>
      (
        await http.post<{ sent: boolean; replyTo: string | null }>(
          '/v1/support/reports',
          // Le compte et le navigateur sont ajoutés par le serveur ; seule la page part d'ici.
          { message: body, page: window.location.pathname },
        )
      ).data,
  });

  const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

  return (
    <section className="mx-auto grid max-w-3xl gap-5 sm:gap-6">
      <header>
        <h1 className="text-title-md text-heading sm:text-title-lg">Signaler un problème</h1>
        <p className="mt-1 text-sm text-secondary">
          Un souci avec l’application ? Écrivez au service technique Permio, qui l’édite. Pour une question sur
          l’organisation de l’auto-école, écrivez plutôt au secrétariat dans Messages.
        </p>
      </header>

      {send.isSuccess ? (
        <Card className="p-6 text-center sm:p-8">
          <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-success-light text-success">
            <CheckCircle2 size={26} aria-hidden />
          </span>
          <h2 className="text-title-sm text-heading">Signalement envoyé</h2>
          <p role="status" className="mx-auto mt-2 max-w-md text-sm text-secondary">
            Merci. Votre message est parti au service technique Permio.{' '}
            {otherEmail
              ? `Nous vous répondrons par e-mail à ${replyTo}.`
              : send.data.replyTo
                ? `Nous vous répondrons par e-mail à ${send.data.replyTo}.`
                : 'Nous vous répondrons par e-mail.'}
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Button
              onClick={() => {
                setMessage('');
                send.reset();
              }}
            >
              Nouveau signalement
            </Button>
            <Button variant="primary" onClick={onDone}>
              {doneLabel}
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="p-5 sm:p-6">
          <form
            className="grid gap-4"
            aria-label="Signaler un problème"
            onSubmit={(e) => {
              e.preventDefault();
              if (text.length >= MIN_LENGTH && !emailInvalid && !send.isPending) send.mutate();
            }}
          >
            <span className="inline-flex items-center gap-1.5 justify-self-start rounded-pill bg-danger-light px-2.5 py-1 text-[11px] font-semibold text-danger-dark">
              <Bug size={12} aria-hidden />
              Service technique Permio
            </span>

            <div className="grid gap-4 sm:grid-cols-2">
              <ReadOnly label="Nom" value={fullName} />
              <ReadOnly label="Auto-école" value={user?.schoolName ?? ''} />
            </div>
            <TextField
              label="E-mail de réponse"
              type="email"
              autoComplete="email"
              value={email}
              readOnly={send.isPending}
              onChange={(e) => setEmail(e.target.value)}
              hint={otherEmail ? 'Cette adresse sera indiquée en tête de votre message.' : 'Nous vous répondrons à cette adresse.'}
              error={emailInvalid ? 'Adresse e-mail invalide.' : undefined}
              className="h-11 text-[15px]"
            />
            <TextAreaField
              label="Que s’est-il passé ?"
              rows={6}
              required
              minLength={MIN_LENGTH}
              maxLength={MAX_LENGTH}
              placeholder="Ce que vous faisiez, ce qui s’est passé, ce que vous attendiez."
              value={message}
              readOnly={send.isPending}
              onChange={(e) => {
                if (send.isError) send.reset();
                setMessage(e.target.value);
              }}
              className="text-[15px]"
            />
            <p className="text-xs leading-relaxed text-secondary">
              Votre message part par e-mail au service technique Permio, avec votre nom, votre adresse e-mail, votre école,
              la page ouverte et votre navigateur. Rien d’autre n’est joint : ne collez pas de données d’élève dont nous
              n’aurions pas besoin.
            </p>
            {send.isError && (
              <p role="alert" className="rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">
                {studentMessagingError(send.error, `Le signalement n’a pas pu être envoyé. Écrivez-nous à ${SUPPORT_EMAIL}.`)}{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold underline">
                  Ouvrir un e-mail
                </a>
              </p>
            )}
            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                icon={Send}
                loading={send.isPending}
                disabled={text.length < MIN_LENGTH || emailInvalid}
                className="max-sm:w-full"
              >
                {send.isPending ? 'Envoi…' : 'Envoyer le signalement'}
              </Button>
            </div>
          </form>
        </Card>
      )}
    </section>
  );
}
